/**
 * §7.13 NPI Adherence Decay Monitor.
 *
 * NPI compliance is not constant — masking adherence that's 90% week 1
 * may erode to 60% by week 12 (Petherick et al. 2021 Nat Hum Behav).
 * This surface makes that operational: pick decay params + a risk-
 * perception gain + a trust index, see (a) the adherence trajectory,
 * (b) the effective β scaler that produces, (c) the simulated outbreak
 * trajectory using that decaying scaler vs a naive constant-adherence
 * version.
 *
 * Engine plumbing exercised:
 *   - adherenceAt + adherenceTrajectoryWithRisk (decay + risk response)
 *   - composeAdherenceBetaScaler (turns adherence trajectory into BetaScaler)
 *   - trustAdherenceMultiplier (channel-weighted trust)
 *   - simulateSeird with custom BetaScaler
 */

import { useMemo, useState } from 'react';
import { findPathogen, listPathogens } from '@biomax/plugin-pathogen-database';
import { findRegion, listRegions } from '@biomax/plugin-region-database';
import { simulateSeird } from '@biomax/plugin-epidemiology';
import { NPI_CATALOG } from '@biomax/plugin-epi-interventions';
import {
  adherenceAt, adherenceTrajectoryWithRisk,
  composeAdherenceBetaScaler, trustAdherenceMultiplier,
  COMM_MODEL_REFERENCES,
} from '@biomax/plugin-comm-models';
import { Sparkline } from '../sparkline';

export function NpiAdherenceDecay() {
  const pathogens = listPathogens();
  const regions = listRegions();
  const npiIds = Object.keys(NPI_CATALOG);

  const [pathogenId, setPathogenId] = useState<string>('sars-cov-2-wildtype');
  const [regionId, setRegionId] = useState<string>('usa');
  const [npiId, setNpiId] = useState<string>('mask-universal');
  const [initial, setInitial] = useState<number>(0.9);
  const [baseline, setBaseline] = useState<number>(0.5);
  const [halfLifeDays, setHalfLifeDays] = useState<number>(45);
  const [responseGain, setResponseGain] = useState<number>(0.4);
  const [trustGov, setTrustGov] = useState<number>(0.6);

  const view = useMemo(() => {
    const pathogen = findPathogen(pathogenId);
    const region = findRegion(regionId);
    const npi = NPI_CATALOG[npiId];
    if (!pathogen || !region || !npi) return null;

    const gamma = 1 / pathogen.infectiousPeriodDays.value;
    const sigma = 1 / pathogen.latentPeriodDays.value;
    const beta = pathogen.R0.value * gamma;
    const ifr = pathogen.ifrByAgeBand[Math.floor(pathogen.ifrByAgeBand.length / 2)]?.ifr ?? 0.005;
    const N = region.population.total;
    const duration = 180;

    // First-pass: naive constant adherence simulation to produce dailyCases for
    // the risk-perception feedback loop.
    const naiveScaler = (_t: number) => 1 - initial * (1 - npi.betaScale);
    const firstPass = simulateSeird({
      N, I0: 100, beta, gamma, sigma, ifr, durationDays: duration, dtDays: 1,
    }, { betaScaler: naiveScaler });
    const S = firstPass.compartments.S!;
    const dailyCases: number[] = [];
    for (let i = 1; i < S.length; i++) dailyCases.push(Math.max(0, S[i - 1]! - S[i]!));

    // Adherence trajectory with risk-perception feedback (from first-pass cases).
    const trustMult = trustAdherenceMultiplier({
      government: trustGov, healthcare: 0.75, community: 0.65,
      citations: [{ source: 'illustrative — replace with regional survey data in v0.3' }],
    }, 'gov');
    const adherenceCurve = { initial, baseline, halfLifeDays };
    const trajectory = adherenceTrajectoryWithRisk(
      adherenceCurve,
      { dailyCases, population: N, responseGain },
    ).map(a => Math.min(1, a * trustMult));

    // Build BetaScaler from the adherence trajectory + the NPI's intrinsic β scale.
    // composeAdherenceBetaScaler(interventionEffect, trajectory): reduce β by
    // `interventionEffect` × adherence(t). `interventionEffect` = 1 - npi.betaScale
    // (so a mask-universal NPI with betaScale=0.4 has interventionEffect=0.6).
    const decayScaler = composeAdherenceBetaScaler(1 - npi.betaScale, trajectory);

    // Second simulation with the realistic decaying-adherence scaler.
    const treated = simulateSeird({
      N, I0: 100, beta, gamma, sigma, ifr, durationDays: duration, dtDays: 1,
    }, { betaScaler: decayScaler });

    // No-intervention baseline for context.
    const noInter = simulateSeird({
      N, I0: 100, beta, gamma, sigma, ifr, durationDays: duration, dtDays: 1,
    });

    // Naive constant-adherence comparison.
    const naive = simulateSeird({
      N, I0: 100, beta, gamma, sigma, ifr, durationDays: duration, dtDays: 1,
    }, { betaScaler: naiveScaler });

    const trajectory0 = adherenceAt(adherenceCurve, 0) * trustMult;
    const trajectoryEnd = adherenceAt(adherenceCurve, duration) * trustMult;

    return {
      pathogen, region, npi,
      trajectory, decayScaler,
      treated, naive, noInter,
      trajectory0, trajectoryEnd, trustMult,
    };
  }, [pathogenId, regionId, npiId, initial, baseline, halfLifeDays, responseGain, trustGov]);

  if (!view) return <div>Loading…</div>;

  const finalDecay = view.treated.compartments.D!.at(-1)!;
  const finalNaive = view.naive.compartments.D!.at(-1)!;
  const finalNone = view.noInter.compartments.D!.at(-1)!;
  const naivePenalty = finalDecay - finalNaive; // extra deaths from fatigue

  return (
    <div className="surface">
      <div className="surface__header">
        <span className="surface__sec">§7.13</span>
        <h1 className="surface__title">NPI Adherence Decay Monitor</h1>
      </div>
      <div className="surface__sub">
        Pandemic-fatigue model · {view.region.names.english} · {view.npi.label} ·
        every parameter PMID-cited via COMM_MODEL_REFERENCES
      </div>

      <div className="panel">
        <div className="panel__head"><span className="panel__title">Scenario + behavioural parameters</span></div>
        <div className="simctl">
          <label className="simctl__field">
            <span className="simctl__label">Pathogen</span>
            <select value={pathogenId} onChange={e => setPathogenId(e.target.value)}>
              {pathogens.map(p => <option key={p.id} value={p.id}>{p.names.common}</option>)}
            </select>
          </label>
          <label className="simctl__field">
            <span className="simctl__label">Region</span>
            <select value={regionId} onChange={e => setRegionId(e.target.value)}>
              {regions
                .slice()
                .sort((a, b) => a.names.english.localeCompare(b.names.english))
                .map(r => <option key={r.id} value={r.id}>{r.names.english}</option>)}
            </select>
          </label>
          <label className="simctl__field">
            <span className="simctl__label">NPI</span>
            <select value={npiId} onChange={e => setNpiId(e.target.value)}>
              {npiIds.map(id => <option key={id} value={id}>{NPI_CATALOG[id]!.label}</option>)}
            </select>
          </label>
        </div>
        <div className="simctl" style={{ marginTop: 12 }}>
          <label className="simctl__field">
            <span className="simctl__label">Initial adherence · {(initial * 100).toFixed(0)}%</span>
            <input type="range" min="0.3" max="1" step="0.01" value={initial}
              onChange={e => setInitial(parseFloat(e.target.value))} />
          </label>
          <label className="simctl__field">
            <span className="simctl__label">Steady-state baseline · {(baseline * 100).toFixed(0)}%</span>
            <input type="range" min="0" max="1" step="0.01" value={baseline}
              onChange={e => setBaseline(parseFloat(e.target.value))} />
          </label>
          <label className="simctl__field">
            <span className="simctl__label">Decay half-life · {halfLifeDays} days</span>
            <input type="range" min="7" max="180" step="1" value={halfLifeDays}
              onChange={e => setHalfLifeDays(parseInt(e.target.value, 10))} />
          </label>
          <label className="simctl__field">
            <span className="simctl__label">Risk-perception gain · {responseGain.toFixed(2)}</span>
            <input type="range" min="0" max="1" step="0.05" value={responseGain}
              onChange={e => setResponseGain(parseFloat(e.target.value))} />
          </label>
          <label className="simctl__field">
            <span className="simctl__label">Government trust · {(trustGov * 100).toFixed(0)}%</span>
            <input type="range" min="0" max="1" step="0.05" value={trustGov}
              onChange={e => setTrustGov(parseFloat(e.target.value))} />
          </label>
        </div>
      </div>

      <div className="kpis">
        <div className="kpi">
          <div className="kpi__label">Adherence day 0</div>
          <div className="kpi__value kpi__value--accent">{(view.trajectory0 * 100).toFixed(0)}%</div>
          <div className="kpi__sub">trust-multiplier × initial</div>
        </div>
        <div className="kpi">
          <div className="kpi__label">Adherence day 180</div>
          <div className="kpi__value">{(view.trajectoryEnd * 100).toFixed(0)}%</div>
          <div className="kpi__sub">exponential decay → baseline</div>
        </div>
        <div className="kpi">
          <div className="kpi__label">Deaths · decaying adherence</div>
          <div className="kpi__value kpi__value--danger">{Math.round(finalDecay).toLocaleString()}</div>
          <div className="kpi__sub">realistic · fatigue penalty +{Math.round(naivePenalty).toLocaleString()} vs naive</div>
        </div>
        <div className="kpi">
          <div className="kpi__label">Deaths · naive constant</div>
          <div className="kpi__value kpi__value--signal">{Math.round(finalNaive).toLocaleString()}</div>
          <div className="kpi__sub">over-optimistic</div>
        </div>
        <div className="kpi">
          <div className="kpi__label">No-intervention deaths</div>
          <div className="kpi__value">{Math.round(finalNone).toLocaleString()}</div>
          <div className="kpi__sub">benchmark</div>
        </div>
      </div>

      <div className="panel">
        <div className="panel__head">
          <span className="panel__title">Adherence trajectory (risk-modulated)</span>
          <span className="panel__meta">[0, 1] · exp decay + case-spike feedback</span>
        </div>
        <Sparkline values={view.trajectory} label="Effective adherence" stroke="var(--bm-accent)" />
      </div>

      <div className="panel">
        <div className="panel__head">
          <span className="panel__title">Cumulative deaths · three scenarios</span>
        </div>
        <Sparkline values={view.noInter.compartments.D!} label="No intervention (D_none)" stroke="var(--bm-danger)" />
        <Sparkline values={view.treated.compartments.D!} label="Decaying adherence (D_decay)" stroke="var(--bm-signal)" />
        <Sparkline values={view.naive.compartments.D!} label="Naive constant adherence (D_naive)" stroke="var(--bm-accent)" />
      </div>

      <div className="panel">
        <div className="panel__head">
          <span className="panel__title">Verified citations</span>
        </div>
        <table className="dtable">
          <thead>
            <tr><th>ID</th><th>Paper</th><th>PMID</th><th>Backs</th></tr>
          </thead>
          <tbody>
            {COMM_MODEL_REFERENCES.map(r => (
              <tr key={r.id}>
                <td>{r.id}</td>
                <td style={{ fontSize: 12 }}>{r.label}</td>
                <td className="num">{r.pmid ?? '—'}</td>
                <td style={{ fontSize: 11, color: 'var(--bm-text-muted)' }}>{r.appliesTo}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
